// frontend/src/composables/useNotificationsRealtime.ts
//
// Layer 1 — realtime для колокола уведомлений.
//
// Бэкенд уже бродкастит каждое платформенное уведомление в WS-группу
// `notifications_<user.id>` (backend/PlatformaAPP/notifications/tasks.py →
// group_send), а singleton-сокет `useNotificationSocket` уже умеет мультиплекс
// этой группы между ChatsPage и колоколом. Не хватало ровно одного провода:
// подписать стор уведомлений на сокет — что и делает этот композабл.
//
// Почему отдельный композабл, а не код в NotificationComponent.vue:
//   * оркестрация (подписка/дебаунс/реконсиляция) тестируется без Vuetify/
//     router (DIP — зависим от абстракций store.pushFromSocket /
//     refreshFromServer и socket.onMessage, не от конкретных компонентов);
//   * колокол остаётся тонким (рендер + polling-fallback).
//
// Polling в NotificationComponent.vue ОСТАЁТСЯ как safety-net (WS оборвался /
// прокся гасит upgrade / cookie протух) — это два независимых пути доставки.

import { getCurrentInstance, onBeforeUnmount, ref, type Ref } from 'vue';
import { useNotificationSocket } from '@/composables/useNotificationSocket';
import { publish } from '@/shared/lib/invalidationBus';
import { isCalendarEventType } from '@/shared/lib/calendarEvents';

/** Минимальный контракт стора, нужный realtime-композаблу (DIP). */
export interface NotificationsRealtimeStore {
    /** Принять WS-пуш. true — payload опознан как bell-уведомление и принят. */
    pushFromSocket(payload: Record<string, unknown>): boolean;
    /** Синхронизировать список+счётчик с сервером (полная форма карточки). */
    refreshFromServer(): Promise<void> | void;
}

export interface UseNotificationsRealtimeReturn {
    /** Подписаться на WS-канал (открывает singleton-сокет при первом вызове). */
    start(): void;
    /** Отписаться (закрывает сокет, если других подписчиков нет). */
    stop(): void;
    /**
     * L1 — реактивный флаг «WS не встаёт» (прокся/AV режут upgrade). Колокол
     * вешает на него учащённый polling (safety-net), пока realtime недоступен.
     */
    degraded: Ref<boolean>;
}

// WS присылает СОКРАЩЁННЫЙ snapshot (без cta/category/точных timestamps и без
// серверного счётчика). Карточка показывается мгновенно (pushFromSocket), а
// полную форму + точный badge подтягиваем одним дебаунс-реквестом после всплеска.
const RECONCILE_DEBOUNCE_MS = 1200;

/**
 * Связывает singleton-WS уведомлений со стором: мгновенный показ пуша +
 * дебаунс-реконсиляция полной формы с сервера, и догон пропущенного на reconnect.
 */
export function useNotificationsRealtime(
    store: NotificationsRealtimeStore,
): UseNotificationsRealtimeReturn {
    const socket = useNotificationSocket({ autoUnsubscribe: false });
    const degraded = ref(false);

    let offMessage: (() => void) | null = null;
    let offReconnect: (() => void) | null = null;
    let offDegraded: (() => void) | null = null;
    let reconcileTimer: ReturnType<typeof setTimeout> | null = null;

    // L0 — наблюдаемость без новой инфры: структурный console-сигнал + window-
    // событие `notifications:ws-status`, которое можно подцепить к аналитике/
    // мониторингу одним слушателем. Серверный комплемент — доля ответов 101 на
    // /ws/ в nginx-логах (ops, без кода).
    const reportWsStatus = (status: 'degraded' | 'open'): void => {
        if (status === 'degraded') {
            try {
                console.warn(
                    '[notifications] ws-degraded → realtime WS не установился, перешли на учащённый polling',
                );
            } catch {
                /* noop */
            }
        }
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            try {
                window.dispatchEvent(
                    new CustomEvent('notifications:ws-status', { detail: { status } }),
                );
            } catch {
                /* noop */
            }
        }
    };

    const clearReconcile = (): void => {
        if (reconcileTimer !== null) {
            clearTimeout(reconcileTimer);
            reconcileTimer = null;
        }
    };

    const scheduleReconcile = (): void => {
        clearReconcile();
        reconcileTimer = setTimeout(() => {
            reconcileTimer = null;
            void store.refreshFromServer();
        }, RECONCILE_DEBOUNCE_MS);
    };

    const start = (): void => {
        if (offMessage) return; // уже подписаны
        offMessage = socket.onMessage((payload) => {
            // pushFromSocket вернёт false для не-bell payload (чат-пинг с roomId)
            // — тогда это сообщение нас не касается, ничего не делаем.
            if (store.pushFromSocket(payload)) {
                scheduleReconcile();
                // Связь уведомления→календарь: бронь/событие меняет календарь
                // пользователя → инвалидируем док-вкладку «Календарь» (подписчик
                // в App.vue дёрнет markCalendarChanged: счётчик + «новая»-точка).
                if (isCalendarEventType(payload.event_type)) {
                    publish('calendar');
                }
            }
        });
        offReconnect = socket.onReconnect(() => {
            // Коннект восстановлен после обрыва — могли пропустить пуши,
            // синхронизируемся сразу (без дебаунса).
            void store.refreshFromServer();
        });
        offDegraded = socket.onDegradedChange((isDegraded) => {
            if (isDegraded === degraded.value) return;
            degraded.value = isDegraded;
            reportWsStatus(isDegraded ? 'degraded' : 'open');
        });
    };

    const stop = (): void => {
        clearReconcile();
        offMessage?.();
        offMessage = null;
        offReconnect?.();
        offReconnect = null;
        offDegraded?.();
        offDegraded = null;
        degraded.value = false;
    };

    // Авто-очистка при размонтировании компонента. Вне setup()-контекста
    // (юнит-тесты) getCurrentInstance() === null → хук не вешаем, без warning'а.
    if (getCurrentInstance()) {
        onBeforeUnmount(stop);
    }

    return { start, stop, degraded };
}
